import { isValidNZBankNumber } from "@fnzc/nz-bank-account-validator";
import { isValidIRDNumber } from "@fnzc/nz-ird-validator";
import { Box, Button, Checkbox, FormControlLabel, FormHelperText, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useFeatureFlagMonitor } from "@nzfm/app-config";
import { differenceInYears } from "date-fns";
import { FieldArray } from "formik";
import { Fragment, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import * as yup from "yup";
import {
  AdviceDelivery,
  AdviceStatuses,
  ApplicationClientRoles,
  baseOtherTaxCountrySchema,
  clientPersonalInfoSchema,
  CountryResidedDto,
  IncomeGeneratorApplicationDto,
  InvestmentOptions,
  KiwiSaverApplicationDto,
  OtherTaxCountryDto,
  Product,
  WealthBuilderApplicationDto,
} from "../../api";
import { bankAccountHasNonPersonalTerms, getApplications, getPrimaryClient, isBnzBankAccount, isJointApplicationClient } from "../../common/helpers";
import {
  Client,
  countries,
  FLAG_SKIP_NCC_SCREEN,
  incomeBands,
  investmentMethodOptions,
  investmentOptionNames,
  investmentPurposeOptions,
  ScreenProps,
  SendApplicationToClientResult,
  titles,
  wealthBands,
  yesNoOptions,
} from "../../common/types";
import { Colors, HintText, ProductIcon, SectionTitleText, SubtitleText, TitleText } from "../../components/controls/helpers";
import { AddressAutocomplete, Autocomplete, BankAccountNumberTextInput, DateTextInput, NumberTextInput, RadioList, Select, TextInput } from "../../components/controls/inputs";
import { useAppDispatch, useAppSelector } from "../../store";
import { clearClient, clearUpsertClientResult, getClientAsync, saveClient, setMessage, upsertClientAsync } from "../../store/adviser";
import { updateAsync } from "../../store/clientApplication";
import { hasSelectedProductRoles } from "../adviser/Helpers";
import FormikWrapper from "../containers/FormikWrapper";
import CalculateTaxRateDialog from "./CalculateTaxRateDialog";
import SendApplicationToClient from "./SendApplicationToClient";

enum FormMode {
  None,
  Confirm,
  Submit,
  Send,
  Get,
}

const tinReasons = ["Country does not issue TINs", "I have not been issued with a TIN", "Country does not require TIN collection"];

const ClientDetails = ({ inBrowserApp, match, nextRoute, prevRoute, route, history }: RouteComponentProps & ScreenProps) => {
  const dispatch = useAppDispatch();
  const { client: adviserClient, upsertClientResult } = useAppSelector((state) => state.adviser);
  const { client, nccList } = useAppSelector((state) => state.clientApplication);
  const [formMode, setFormMode] = useState<FormMode>(FormMode.None);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [isManualPostalAddress, setIsManualPostalAddress] = useState(false);
  const [prevScreen, setPrevScreen] = useState(false);
  const [helpMeCalculate, setHelpMeCalculate] = useState(false);
  const _client = adviserClient || client;
  const [postalAddressDifferent, setPostalAddressDifferent] = useState(!!_client?.postalAddress);
  const isJointApplication = _client && isJointApplicationClient(_client);
  const primaryClient = getPrimaryClient(_client);
  const jointAccountApplications = (isJointApplication && getApplications({ ..._client })) || [];
  const wealthBuilderApplications = jointAccountApplications.filter((data) => data.product === Product.WealthBuilder) as WealthBuilderApplicationDto[];
  const incomeGeneratorApplications = jointAccountApplications.filter((data) => data.product === Product.IncomeGenerator) as IncomeGeneratorApplicationDto[];
  const clientId = (match.params as { clientId: string }).clientId;
  const isMobileView = useMediaQuery(useTheme().breakpoints.down("sm"));
  const { user } = useAppSelector((state) => state.oidc);
  const { enabled: skipNccScreenEnabled } = useFeatureFlagMonitor(FLAG_SKIP_NCC_SCREEN);

  const setIsPostal = (values: any, setValues: (values: any) => void, isPostal: boolean) => {
    setValues({ ...values, postalAddressDifferent: isPostal });
  };

  const setManualResidentialAddress = (values: any, setValues: (values: any) => void) => {
    const newIsManualAddress = !isManualAddress;
    setValues({ ...values, residentialAddress: newIsManualAddress ? { streetAddress: "", suburb: "", city: "", postCode: "" } : undefined, isManualAddress: newIsManualAddress });
    setIsManualAddress(newIsManualAddress);
  };

  const setManualPostalAddress = (values: any, setValues: (values: any) => void) => {
    const newIsManualPostalAddress = !isManualPostalAddress;
    setValues({
      ...values,
      postalAddress: newIsManualPostalAddress ? { streetAddress: "", suburb: "", city: "", postCode: "" } : undefined,
      isManualPostalAddress: newIsManualPostalAddress,
    });
    setIsManualPostalAddress(newIsManualPostalAddress);
  };

  const initialValues = () => ({
    firstName: "",
    middleName: "",
    lastName: "",
    mobile: "",
    email: "",
    dateOfBirth: undefined,
    irdNumber: "",
    salutation: "",
    countryOfBirth: "",
    occupation: "",
    residentialAddress: undefined,
    isManualAddress: false,
    isManualPostalAddress: false,
    postalAddressDifferent: !!_client?.postalAddress,
    postalAddress: undefined,
    annualIncome: "",
    estimatedTotalWealth: "",
    isNZTaxResident: undefined,
    pieTaxRate: undefined,
    isUSPerson: undefined,
    taxIdentificationNumber: "",
    isOtherTaxResident: undefined,
    otherTaxCountries: [] as OtherTaxCountryDto[],
    bankAccountName: "",
    bankAccountNumber: "",
    ..._client,
    citizenships: !_client?.citizenships?.length ? [""] : _client.citizenships,
  });

  const cleanValues = (values: Client) => ({
    ...values,
    postalAddress: !postalAddressDifferent ? undefined : values.postalAddress,
    citizenships: values.citizenships?.filter((c) => !!c),
    otherTaxCountries: !values.isOtherTaxResident ? ([] as OtherTaxCountryDto[]) : values.otherTaxCountries?.filter((c) => !!c.country),
    countriesResided:
      skipNccScreenEnabled || (!nccList?.some((ncc) => ncc === values.countryOfBirth) && !values.citizenships?.filter((c) => !!c).find((c) => nccList?.some((ncc) => c === ncc)))
        ? ([] as CountryResidedDto[])
        : values.countriesResided?.filter((c) => !!c.country),
    wealthBuilderApplications: values.wealthBuilderApplications?.map((a) => ({
      ...a,
      annualIncome: a.applicationClients?.some((ac) => ac.role === ApplicationClientRoles.Joint) === true ? a.annualIncome : values.annualIncome,
      estimatedTotalWealth: a.applicationClients?.some((ac) => ac.role === ApplicationClientRoles.Joint) === true ? a.estimatedTotalWealth : values.estimatedTotalWealth,
    })),
    incomeGeneratorApplications: values.incomeGeneratorApplications?.map((a) => ({
      ...a,
      annualIncome: a.applicationClients?.some((ac) => ac.role === ApplicationClientRoles.Joint) === true ? a.annualIncome : values.annualIncome,
      estimatedTotalWealth: a.applicationClients?.some((ac) => ac.role === ApplicationClientRoles.Joint) === true ? a.estimatedTotalWealth : values.estimatedTotalWealth,
    })),
    passport: !values.passport || isBnzBankAccount(values.bankAccountNumber) ? undefined : { ...values.passport },
    driverLicence: !values.driverLicence || isBnzBankAccount(values.bankAccountNumber) ? undefined : { ...values.driverLicence },
  });

  const openSendToClient = (values: Client) => {
    dispatch(saveClient(cleanValues(values)));
    setFormMode(FormMode.Confirm);
  };

  const sendToClient = (result: SendApplicationToClientResult) => {
    dispatch(upsertClientAsync({ ..._client, ...result }));
    setFormMode(FormMode.Send);
  };

  const handleSubmit = (values: Client) => {
    const _values = cleanValues(values);

    if (!inBrowserApp) {
      dispatch(saveClient(_values));

      if (formMode === FormMode.None) {
        if (values?.adviceDelivery === AdviceDelivery.InPerson) {
          dispatch(upsertClientAsync(_values));
          setFormMode(FormMode.Submit);
        } else prevScreen ? prevRoute && prevRoute(_values) : nextRoute(_values);
      }
    } else {
      dispatch(updateAsync({ token: clientId, client: { ..._values, ...(route && { progress: route }) } })).then((result: any) => {
        if (!result.error) nextRoute(_values);
      });
    }
  };

  useEffect(() => {
    if (formMode === FormMode.Submit) {
      if (upsertClientResult?.clientId) {
        (async () => {
          dispatch(clearUpsertClientResult());
          await dispatch(getClientAsync(clientId));
          setFormMode(FormMode.Get);
        })();
      }
    } else if (formMode === FormMode.Get) {
      if (_client?.clientId) {
        setFormMode(FormMode.None);
        prevScreen ? prevRoute && prevRoute(_client) : nextRoute(_client);
      }
    } else if (formMode === FormMode.Send) {
      if (upsertClientResult?.clientId) {
        setFormMode(FormMode.None);
        dispatch(clearClient());
        dispatch(setMessage({ text: "The application has been sent successfully." }));
        history.replace(`/adviser/${clientId}/client-application`);
      }
    }
  }, [_client, upsertClientResult, formMode]);

  return (
    <>
      <FormikWrapper
        initialValues={initialValues()}
        initialTouched={{ bankAccountName: !!_client?.bankAccountName }}
        validationSchema={!inBrowserApp ? adviserSchema : clientSchema(isJointApplication)}
        onSubmit={handleSubmit}
      >
        {({ isValid, setFieldValue, setValues, touched, values }) => (
          <>
            {!inBrowserApp && (
              <Box mb={3}>
                <TitleText hideMarginBottom>Client</TitleText>
                <SubtitleText hideMarginBottom>
                  <Grid container justifyContent="space-between">
                    <Grid item style={{ paddingTop: 3 }}>
                      <span style={{ color: Colors.Black }}>
                        {values.firstName} {values.lastName || ""}{" "}
                      </span>
                      &emsp;{values.adviceStatus === AdviceStatuses.Skipped ? "Product onboarding" : "Savings Advice Tool"}
                    </Grid>
                    <Grid item>
                      {values.products?.includes(Product.KiwiSaver) && <ProductIcon product={Product.KiwiSaver} highlighted />}
                      {values.products?.includes(Product.WealthBuilder) && <ProductIcon product={Product.WealthBuilder} highlighted />}
                      {values.products?.includes(Product.IncomeGenerator) && <ProductIcon product={Product.IncomeGenerator} highlighted />}
                    </Grid>
                  </Grid>
                </SubtitleText>
              </Box>
            )}
            {isJointApplication && (
              <>
                <Box mb={3}>
                  <Typography variant="h6">
                    Hi {_client?.firstName}, {primaryClient?.firstName} has requested to open a joint account with you. Before getting you set up, here is some information on what
                    they have chosen:
                  </Typography>
                </Box>
                {wealthBuilderApplications.map((application, index) => (
                  <Box mb={3} key={index}>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Wealth Builder: </b>Joint account
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Account nickname: </b>
                        {application.nickname}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Investment option: </b>
                        {investmentOptionNames[application.investmentOption!]}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    {application?.investmentOption === InvestmentOptions.SelfSelect && (
                      <Box display="flex" justifyContent="space-between">
                        <SubtitleText hideMarginBottom black>
                          Income: {application.income || 0}% Inflation: {application.inflation || 0}% Growth: {application.growth || 0}%
                        </SubtitleText>
                      </Box>
                    )}
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Investment purpose: </b>
                        {investmentPurposeOptions.find((ip) => ip.value === application.investmentPurpose)?.label ?? "Other"}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Investment method: </b>
                        {investmentMethodOptions.find((im) => im.value === application.investmentMethods)?.label ?? ""}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Estimated net annual income: </b>
                        {incomeBands.find((im) => im.value === application.annualIncome)?.label ?? ""}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Estimated net wealth: </b>
                        {wealthBands.find((im) => im.value === application.estimatedTotalWealth)?.label ?? ""}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Bank account name: </b>
                        {values.bankAccountName}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Bank account number: </b>
                        {values.bankAccountNumber}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                  </Box>
                ))}
                {incomeGeneratorApplications.map((application, index) => (
                  <Box mb={3} key={index}>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Income Generator: </b>Joint account
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Account nickname: </b>
                        {application.nickname}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Distribution option: </b>
                        {application.distributionOption}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Investment purpose: </b>
                        {investmentPurposeOptions.find((ip) => ip.value === application.investmentPurpose)?.label ?? "Other"}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Investment method: </b>
                        {investmentMethodOptions.find((im) => im.value === application.investmentMethods)?.label ?? ""}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Estimated net annual income: </b>
                        {incomeBands.find((im) => im.value === application.annualIncome)?.label ?? ""}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Estimated net wealth: </b>
                        {wealthBands.find((im) => im.value === application.estimatedTotalWealth)?.label ?? ""}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Bank account name: </b>
                        {values.bankAccountName}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <SubtitleText hideMarginBottom black>
                        <b>Bank account number: </b>
                        {values.bankAccountNumber}
                      </SubtitleText>
                      <CheckIcon />
                    </Box>
                  </Box>
                ))}
              </>
            )}
            <Box mb={3}>
              <SectionTitleText hideMarginTop>Personal information</SectionTitleText>
              <Grid container spacing={3}>
                {(inBrowserApp || values.adviceDelivery === AdviceDelivery.InPerson) && (
                  <>
                    <Grid item md={3} sm={6} xs={12}>
                      <Select name="salutation" label="Title" options={titles} />
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                      <TextInput placeholder={`First name as per ${inBrowserApp ? "your" : `client's`} ID`} label="First name" name="firstName" />
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                      <TextInput placeholder={`Middle name as per ${inBrowserApp ? "your" : `client's`} ID`} label="Middle name(s)" name="middleName" />
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                      <TextInput placeholder={`Last name as per ${inBrowserApp ? "your" : `client's`} ID`} label="Last name" name="lastName" />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextInput label="Email" name="email" disabled />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <NumberTextInput setStringValue label="Mobile" name="mobile" mask="999999999999" />
                    </Grid>
                  </>
                )}
                <Grid item md={6} xs={12}>
                  <DateTextInput name="dateOfBirth" label="Date of birth" />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextInput label="Occupation" name="occupation" />
                </Grid>
                {!isManualAddress && (
                  <Grid item xs={12}>
                    <AddressAutocomplete label="Residential address (not PO Box)" name="residentialAddress" />
                  </Grid>
                )}
                {!values.residentialAddress && (
                  <Grid item xs={12}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setManualResidentialAddress(values, setValues);
                        return false;
                      }}
                    >
                      Can't find {inBrowserApp ? "your" : "their"} address?
                    </a>
                  </Grid>
                )}
                {isManualAddress && (
                  <>
                    <Grid item md={6} xs={12}>
                      <TextInput label="Residential street address" name="residentialAddress.streetAddress" />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextInput label="Suburb" name="residentialAddress.suburb" />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextInput label="City/Town" name="residentialAddress.city" />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextInput label="Postcode" name="residentialAddress.postCode" />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextInput label="Country" name="residentialAddress.country" />
                    </Grid>
                    <Grid item xs={12}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setManualResidentialAddress(values, setValues);
                          return false;
                        }}
                      >
                        Find {inBrowserApp ? "your" : "their"} address
                      </a>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={postalAddressDifferent}
                        onChange={() => {
                          setPostalAddressDifferent(!postalAddressDifferent);
                          setIsPostal(values, setValues, !postalAddressDifferent);
                        }}
                      />
                    }
                    label={
                      <SubtitleText black hideMarginBottom>
                        Postal address is different from residential address.
                      </SubtitleText>
                    }
                    labelPlacement="end"
                  />
                </Grid>
                {!isManualPostalAddress && postalAddressDifferent && (
                  <Grid item xs={12}>
                    <AddressAutocomplete label="Postal address" name="postalAddress" />
                  </Grid>
                )}
                {!values.postalAddress && postalAddressDifferent && (
                  <Grid item xs={12}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setManualPostalAddress(values, setValues);
                        return false;
                      }}
                    >
                      Can't find {inBrowserApp ? "your" : "their"} postal address?
                    </a>
                  </Grid>
                )}
                {isManualPostalAddress && postalAddressDifferent && (
                  <>
                    <Grid item md={6} xs={12}>
                      <TextInput label="Postal street address" name="postalAddress.streetAddress" />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextInput label="Suburb" name="postalAddress.suburb" />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextInput label="City/Town" name="postalAddress.city" />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextInput label="Postcode" name="postalAddress.postCode" />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <TextInput label="Country" name="postalAddress.country" />
                    </Grid>
                    <Grid item xs={12}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setManualPostalAddress(values, setValues);
                          return false;
                        }}
                      >
                        Find {inBrowserApp ? "your" : "their"} postal address
                      </a>
                    </Grid>
                  </>
                )}
                <Grid item md={6} xs={12}>
                  <Autocomplete name="countryOfBirth" label="Country of birth" options={countries} endAdornment={<ExpandMoreIcon />} />
                </Grid>
                <FieldArray
                  name="citizenships"
                  render={(citizenshipsArrayHelpers) => (
                    <>
                      {values.citizenships?.map((citizenship, index) => (
                        <Grid item md={6} xs={12} key={`citizenships${citizenship}`}>
                          <Grid container alignItems="flex-end" spacing={3}>
                            <Grid item xs>
                              <Autocomplete
                                name={`citizenships[${index}]`}
                                label="Citizenship"
                                options={countries.filter((country) => country === citizenship || !values.citizenships?.includes(country))}
                                endAdornment={<ExpandMoreIcon />}
                              />
                            </Grid>
                            {index > 0 && (
                              <Grid item>
                                <IconButton color="inherit" onClick={(_) => citizenshipsArrayHelpers.remove(index)}>
                                  <ClearIcon />
                                </IconButton>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      ))}
                      <Grid item md={values.citizenships?.length && values.citizenships.length % 2 === 0 ? 6 : 12} xs={12}>
                        <Grid container alignItems="flex-end" justifyContent="flex-end" style={{ height: "100%" }}>
                          <Button variant="contained" color="primary" onClick={(_) => citizenshipsArrayHelpers.push("")}>
                            Add citizenship
                          </Button>
                        </Grid>
                      </Grid>
                    </>
                  )}
                />
                {!isJointApplication && (
                  <>
                    <Grid item md={6} xs={12}>
                      <Select name="annualIncome" label="Annual income" options={incomeBands} />
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <Select
                        name="estimatedTotalWealth"
                        label="Estimated personal wealth"
                        tooltip={`Please estimate ${inBrowserApp ? "your" : `the client's`} personal wealth (excluding property and business assets)`}
                        options={wealthBands}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
            {!isJointApplication && (
              <Box mb={3}>
                <SectionTitleText>Bank details</SectionTitleText>
                <SubtitleText>
                  Please provide a bank account in {inBrowserApp ? `your name` : `the name of the applicant(s)`}. All deposits should be made from this nominated account and all
                  withdrawals will be made to this account.
                  <br />
                  <br />
                  {!inBrowserApp &&
                    `Note this should be the exact name on their bank statement. If you enter a BNZ account, they will be required to go through Liveness verification.`}
                </SubtitleText>
                <Grid container spacing={3}>
                  <Grid item md={6} xs={12}>
                    <TextInput label="Bank account name" name="bankAccountName" />
                    {!!values.bankAccountName && touched.bankAccountName && bankAccountHasNonPersonalTerms(values.bankAccountName, values.lastName) && (
                      <FormHelperText error>
                        Warning: {inBrowserApp ? "Your" : `The applicant's`} bank account needs to be in {inBrowserApp ? "your" : "their"} name.
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <BankAccountNumberTextInput label="Bank account number" name="bankAccountNumber" />
                  </Grid>
                </Grid>
              </Box>
            )}
            <Box mb={3}>
              <SectionTitleText>Tax</SectionTitleText>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <RadioList
                    horizontal
                    name="isNZTaxResident"
                    label={`Are ${inBrowserApp ? "you" : "they"} a New Zealand tax resident?`}
                    options={yesNoOptions}
                    onChange={(option) => !option && setHelpMeCalculate(false)}
                  />
                </Grid>
                {(inBrowserApp || (!inBrowserApp && values.adviceDelivery === AdviceDelivery.InPerson)) && (
                  <Grid item md={6} xs={12}>
                    <NumberTextInput label="IRD number" name="irdNumber" mask="999999999" setStringValue />
                  </Grid>
                )}
                <Grid item md={6} xs={12}>
                  <Grid item xs={12}>
                    <Select
                      label="Prescribed Investor Rate (PIR)"
                      name="pieTaxRate"
                      options={
                        values.isNZTaxResident
                          ? [
                              { label: "10.5%", value: 0.105 },
                              { label: "17.5%", value: 0.175 },
                              { label: "28%", value: 0.28 },
                            ]
                          : [{ label: "28%", value: 0.28 }]
                      }
                    />
                  </Grid>
                </Grid>
                {(inBrowserApp || (!inBrowserApp && values.adviceDelivery === AdviceDelivery.InPerson)) && (
                  <Grid item md={6} xs={12} style={{ paddingTop: isMobileView ? 0 : 35 }}>
                    {values.isNZTaxResident && (
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setHelpMeCalculate(true);
                          return false;
                        }}
                      >
                        Help me calculate
                      </a>
                    )}
                    <HintText hideMarginTop>The rate you elect will apply to all of your NZ Funds accounts.</HintText>
                  </Grid>
                )}
                <CalculateTaxRateDialog ok={() => setHelpMeCalculate(false)} open={helpMeCalculate} />
                <Grid item md={6} xs={12}>
                  <RadioList
                    horizontal
                    name="isUSPerson"
                    label={`Are ${inBrowserApp ? "you" : "they"} a US person?`}
                    tooltip="A US person includes a US tax resident, citizen, or permanent resident/Green Card holder."
                    options={yesNoOptions}
                  />
                </Grid>
                {values.isUSPerson && (
                  <Grid item md={6} xs={12}>
                    <NumberTextInput label="Tax Identification Number (TIN)" name="taxIdentificationNumber" mask="999999999" setStringValue />
                  </Grid>
                )}
                <FieldArray
                  name="otherTaxCountries"
                  render={(arrayHelpers) => (
                    <>
                      <Grid item xs={12}>
                        <RadioList
                          horizontal
                          name="isOtherTaxResident"
                          label={`Are ${inBrowserApp ? "you" : "they"} a tax resident of another country?`}
                          options={yesNoOptions}
                          onChange={(selected) => {
                            if (selected) arrayHelpers.push({ country: "", tin: "", reason: undefined });
                            else setFieldValue("otherTaxCountries", [] as OtherTaxCountryDto[]);
                          }}
                        />
                      </Grid>
                      {values.otherTaxCountries?.map((countryOfTaxResidence, index) => (
                        <Fragment key={index}>
                          <Grid item md={6} xs={12}>
                            <Autocomplete
                              name={`otherTaxCountries[${index}].country`}
                              label="Country of tax residence"
                              options={countries.filter((c) => c !== "New Zealand")}
                              endAdornment={<ExpandMoreIcon />}
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <Grid container alignItems="flex-end" spacing={3}>
                              <Grid item xs>
                                {countryOfTaxResidence.reason === undefined && (
                                  <TextInput
                                    label="Tax Identification Number (TIN)"
                                    name={`otherTaxCountries[${index}].tin`}
                                    onChange={(value) => !!countryOfTaxResidence.tin && value !== "" && setFieldValue(`otherTaxCountries[${index}].reason`, undefined)}
                                  />
                                )}
                                {countryOfTaxResidence.reason !== undefined && (
                                  <Autocomplete name={`otherTaxCountries[${index}].reason`} label="TIN unavailable reason" options={tinReasons} endAdornment={<ExpandMoreIcon />} />
                                )}
                              </Grid>
                              {index > 0 && (
                                <Grid item>
                                  <IconButton color="inherit" onClick={(_) => arrayHelpers.remove(index)}>
                                    <ClearIcon />
                                  </IconButton>
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                          <Grid item md={6} xs={12}>
                            <FormControlLabel
                              label={
                                <SubtitleText black hideMarginBottom>
                                  TIN unavailable.
                                </SubtitleText>
                              }
                              labelPlacement="end"
                              control={
                                <Checkbox
                                  color="primary"
                                  checked={countryOfTaxResidence.reason !== undefined}
                                  onChange={(_, checked) => {
                                    setFieldValue(`otherTaxCountries[${index}].reason`, checked ? "" : undefined);
                                    setFieldValue(`otherTaxCountries[${index}].tin`, checked ? undefined : "");
                                  }}
                                />
                              }
                            />
                          </Grid>
                          <Grid item md={6} xs={12}>
                            {index + 1 === values.otherTaxCountries?.length && (
                              <Grid container justifyContent="flex-end">
                                <Button variant="contained" color="primary" onClick={(_) => arrayHelpers.push({ country: "", tin: "", reason: undefined })}>
                                  Add tax residence country
                                </Button>
                              </Grid>
                            )}
                          </Grid>
                        </Fragment>
                      ))}
                    </>
                  )}
                />
              </Grid>
            </Box>
            <Grid container spacing={2} justifyContent="space-between">
              <Grid item>
                {!!prevRoute && inBrowserApp && !isJointApplication && (
                  <Button variant="contained" type="button" onClick={() => prevRoute(values)}>
                    Previous
                  </Button>
                )}
                {!!prevRoute && !inBrowserApp && (
                  <Button variant="contained" type="submit" disabled={formMode !== FormMode.None} onClick={() => setPrevScreen(true)}>
                    Previous
                  </Button>
                )}
              </Grid>
              <Grid item>
                <Grid container spacing={2}>
                  {!inBrowserApp && _client?.adviceDelivery === AdviceDelivery.InPerson && hasSelectedProductRoles(user, values?.products) && (
                    <Grid item>
                      <Button variant="contained" type="button" disabled={formMode !== FormMode.None} onClick={() => isValid && openSendToClient(values)}>
                        Send to client
                      </Button>
                    </Grid>
                  )}
                  <Grid item>
                    <Button variant="contained" color="primary" type="submit" disabled={formMode !== FormMode.None}>
                      Next
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </FormikWrapper>
      <SendApplicationToClient hasMobile={!!_client?.mobile} open={formMode === FormMode.Confirm} cancel={() => setFormMode(FormMode.None)} ok={sendToClient} />
    </>
  );
};

const adviserSchema = yup.object({
  dateOfBirth: yup
    .date()
    .test("DOB valid", "Must be 100 years or less", (value) => !value || differenceInYears(new Date(), value || new Date()) <= 100)
    .when("kiwiSaverApplication", {
      is: (val?: KiwiSaverApplicationDto) => !!val?.active,
      then: yup.date().test("DOB valid", "Must be 18 years or over", (value) => !value || differenceInYears(new Date(), value || new Date()) >= 18),
    })
    .when("kiwiSaverApplication", {
      is: (val?: KiwiSaverApplicationDto) => !val?.active,
      then: yup.date().test("DOB valid", "Must be 16 years or over", (value) => !value || differenceInYears(new Date(), value || new Date()) >= 16),
    }),
});

const clientSchema = (isJointApplication?: boolean) =>
  clientPersonalInfoSchema.concat(
    yup.object({
      irdNumber: yup
        .string()
        .required("IRD number is required")
        .test("IRD valid", "IRD number is invalid", (value) => !!value && isValidIRDNumber(value)),
      isNZTaxResident: yup.boolean().required("A response is required"),
      isUSPerson: yup.boolean().required("A response is required"),
      taxIdentificationNumber: yup.string().when("isUSPerson", {
        is: true,
        then: yup.string().required("Tax Identification Number is required"),
        otherwise: yup.string(),
      }),
      isOtherTaxResident: yup.boolean().required("A response is required"),
      pieTaxRate: yup
        .number()
        .required("PIR is required")
        .when("isNZTaxResident", {
          is: true,
          then: yup.number().oneOf([0.105, 0.175, 0.28], "PIR is required"),
        })
        .when("isNZTaxResident", {
          is: false,
          then: yup.number().oneOf([0.28], "PIR is required"),
        }),
      otherTaxCountries: yup
        .array()
        .of(baseOtherTaxCountrySchema)
        .when("isOtherTaxResident", {
          is: true,
          then: yup
            .array()
            .of(
              baseOtherTaxCountrySchema.concat(
                yup.object().shape(
                  {
                    country: yup.string().required("Country of tax residence is required"),
                    tin: yup.string().when("reason", {
                      is: undefined,
                      then: yup.string().required("TIN is required"),
                    }),
                    reason: yup.string().when("tin", {
                      is: undefined,
                      then: yup.string().required("Reason is required"),
                    }),
                  },
                  [["tin", "reason"]]
                )
              )
            )
            .min(1),
        }),
      bankAccountName: isJointApplication ? yup.string() : yup.string().required("Account name is required"),
      bankAccountNumber: isJointApplication
        ? yup.string()
        : yup
            .string()
            .required("Account number is required")
            .test("Account number valid", "Bank account is invalid", (value) => {
              const _values = value?.split("-");
              return !!_values && isValidNZBankNumber(_values[0], _values[1], _values[2], _values[3]);
            }),
      annualIncome: isJointApplication ? yup.string() : yup.string().required("Estimated net annual income is required"),
      estimatedTotalWealth: isJointApplication ? yup.string() : yup.string().required("Estimated net wealth is required"),
    })
  );

export default ClientDetails;
